import { DeleteOutlineOutlined, UploadFileOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState, type ChangeEvent } from 'react';
import { errorCode } from '../model/resource';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { FeedbackSnackbar, FormDialog, HelpPopover, StatusBadge } from '../../../design-system';
type CertificateSealControlProps = {
  port: TenantSettingsPort;
  companyId: string;
  companyLabel: string;
  fixtureMode: boolean;
  readOnly: boolean;
  disabled: boolean;
  sealConfigured: boolean;
  onConfiguredChange: (configured: boolean) => void;
};
const maxSealBytes = 8 * 1024 * 1024;
function uploadErrorMessage(cause: unknown) {
  if (errorCode(cause) === 'IDEMPOTENCY_IN_PROGRESS') {
    return '같은 직인 등록 요청을 처리하고 있습니다. 잠시 후 다시 시도해 주세요.';
  }
  if (errorCode(cause) === 'IDEMPOTENCY_CONFLICT') {
    return '선택한 파일과 등록 요청이 일치하지 않습니다. 다시 선택한 뒤 업로드해 주세요.';
  }
  return cause instanceof Error ? cause.message : '직인 파일을 등록하지 못했습니다.';
}
export function CertificateSealControl({
  port,
  companyId,
  companyLabel,
  fixtureMode,
  readOnly,
  disabled,
  sealConfigured,
  onConfiguredChange,
}: CertificateSealControlProps) {
  const [busy, setBusy] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [uploadIdempotencyKey, setUploadIdempotencyKey] = useState<string>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileName, setFileName] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!sealConfigured) setFileName(undefined);
  }, [sealConfigured]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );
  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || readOnly || disabled || busy) return;
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > maxSealBytes) {
      setNotice(undefined);
      setError('직인은 8MB 이하 PNG 또는 JPG 이미지로 등록해 주세요.');
      return;
    }
    setSelectedFile(file);
    setUploadIdempotencyKey(crypto.randomUUID());
    setPreviewUrl(URL.createObjectURL(file));
    setPreviewOpen(true);
    setNotice(undefined);
    setError(undefined);
  };
  const closePreview = () => {
    if (busy) return;
    setSelectedFile(undefined);
    setUploadIdempotencyKey(undefined);
    setPreviewUrl(undefined);
    setPreviewOpen(false);
    setError(undefined);
  };
  const upload = () => {
    if (!selectedFile || !previewUrl || !uploadIdempotencyKey || readOnly || disabled || busy) return;
    setBusy(true);
    setNotice(undefined);
    setError(undefined);
    const request = fixtureMode
      ? Promise.resolve({
          sealConfigured: true,
        })
      : port.uploadCompanyCertificateSeal(companyId, selectedFile, uploadIdempotencyKey);
    void request
      .then(
        (status) => {
          onConfiguredChange(status.sealConfigured);
          setFileName(selectedFile.name);
          setSelectedFile(undefined);
          setUploadIdempotencyKey(undefined);
          setPreviewUrl(undefined);
          setPreviewOpen(false);
          setNotice(sealConfigured ? '직인 파일을 교체했습니다.' : '직인 파일을 등록했습니다.');
        },
        (cause: unknown) => {
          setError(uploadErrorMessage(cause));
        },
      )
      .finally(() => setBusy(false));
  };
  const deactivate = () => {
    if (readOnly || disabled || busy) return;
    setBusy(true);
    setNotice(undefined);
    setError(undefined);
    const request = fixtureMode
      ? Promise.resolve({
          sealConfigured: false,
        })
      : port.deleteCompanyCertificateSeal(companyId);
    void request
      .then(
        (status) => {
          onConfiguredChange(status.sealConfigured);
          setFileName(undefined);
          setDeactivateOpen(false);
          setNotice('직인 사용을 중지했습니다.');
        },
        (cause: unknown) => {
          setError(cause instanceof Error ? cause.message : '직인 사용을 중지하지 못했습니다.');
        },
      )
      .finally(() => setBusy(false));
  };
  return (
    <Box data-testid="certificate-seal-control">
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <VerifiedOutlined color="action" fontSize="small" />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 750,
            }}
          >
            증명서 직인
          </Typography>
          <HelpPopover title="증명서 직인">직인 파일은 선택한 법인에만 적용되며 증명서 전용 암호화 저장소에 보관됩니다. 사용을 중지해도 기존 파일과 변경 이력은 감사 목적으로 보존됩니다. 기존 직인 원본은 화면에 표시하지 않습니다.</HelpPopover>
        </Stack>
        <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1.5}
            sx={{
              alignItems: {
                xs: 'stretch',
                sm: 'center',
              },
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  직인 파일
                </Typography>
                <StatusBadge
                  tone={sealConfigured ? 'success' : 'neutral'}
                  label={sealConfigured ? '등록됨' : '미등록'}
                />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  overflowWrap: 'anywhere',
                }}
              >
                {sealConfigured
                  ? fileName || `${companyLabel}에 등록된 직인 파일`
                  : `${companyLabel}에 등록된 직인이 없습니다.`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PNG, JPG · 최대 8MB
              </Typography>
            </Box>
            {!readOnly ? (
              <Stack direction="row" spacing={1}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileOutlined />}
                  disabled={disabled || busy}
                >
                  {sealConfigured ? '직인 교체' : '직인 업로드'}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg"
                    aria-label="직인 파일 선택"
                    onChange={selectFile}
                  />
                </Button>
                {sealConfigured ? (
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteOutlineOutlined />}
                    disabled={disabled || busy}
                    onClick={() => setDeactivateOpen(true)}
                  >
                    직인 사용 중지
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
      {!readOnly ? (
        <FormDialog
          open={previewOpen}
          onClose={closePreview}
          onSubmit={upload}
          title="직인 확인"
          cancelLabel="다시 선택"
          submitLabel="이 직인 사용"
          busy={busy}
          busyLabel="저장 중…"
          submitDisabled={!selectedFile}
          maxWidth="xs"
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              pt: 1,
            }}
          >
            <Box
              sx={{
                width: 200,
                height: 200,
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="선택한 직인 미리보기"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : null}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: 'center',
              }}
            >
              현재 브라우저에서 선택한 파일만 확인합니다. 기존에 등록한 직인 원본은 화면으로 불러오지 않습니다.
            </Typography>
            {error ? (
              <Alert
                role="alert"
                severity="error"
                sx={{
                  width: '100%',
                }}
              >
                {error}
              </Alert>
            ) : null}
          </Stack>
        </FormDialog>
      ) : null}
      {!readOnly ? (
        <FormDialog
          open={deactivateOpen}
          onClose={() => setDeactivateOpen(false)}
          onSubmit={deactivate}
          title={`${companyLabel} 직인 사용 중지`}
          cancelLabel="취소"
          submitLabel="사용 중지"
          busy={busy}
          busyLabel="처리 중…"
          submitColor="error"
          maxWidth="xs"
        >
          <Stack spacing={1.5}>
            <Alert severity="warning">
              사용을 중지하면 이 법인 소속 구성원의 직인 필수 증명서는 새로 발급할 수 없습니다. 기존 파일과 변경 이력은
              감사 목적으로 보존됩니다.
            </Alert>
            {error ? (
              <Alert role="alert" severity="error">
                {error}
              </Alert>
            ) : null}
          </Stack>
        </FormDialog>
      ) : null}
    </Box>
  );
}
